import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, UserPlus, Loader2, CalendarDays, Wallet } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * HrDashboard — Phase 5 · Attendance & Salary Monitor.
 *
 * L-1 · all human-readable labels flow through `useI18n().t("hr.*",
 * "English fallback")` so the same page reads Hindi / Arabic / Spanish /
 * Chinese / Japanese when the user picks that language. Internal status
 * IDs (`present`, `half_day`, …) stay stable — the backend + calculator
 * are never affected.
 */
const STATUS_KEYS = ["present", "absent", "half_day",
                      "paid_leave", "unpaid_leave", "holiday"];
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function HrDashboard() {
  const { t } = useI18n();
  const [tab, setTab] = useState("employer");
  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="hr-dashboard">
      <div className="flex items-center gap-2 mb-4">
        <h1 className="text-2xl font-black text-stone-900">{t("hr.title", "Attendance & Salary")}</h1>
        <span className="text-[10px] uppercase tracking-widest text-stone-400 font-black">HR-1</span>
      </div>
      <div className="inline-flex rounded-full bg-stone-100 p-1 text-[12px] font-black mb-4" role="tablist">
        <button className={`px-4 py-1.5 rounded-full ${tab === "employer" ? "bg-white text-stone-900 shadow" : "text-stone-500"}`}
                onClick={() => setTab("employer")} data-testid="hr-tab-employer">{t("hr.employer","Employer")}</button>
        <button className={`px-4 py-1.5 rounded-full ${tab === "employee" ? "bg-white text-stone-900 shadow" : "text-stone-500"}`}
                onClick={() => setTab("employee")} data-testid="hr-tab-employee">{t("hr.my_work","My work")}</button>
      </div>
      {tab === "employer" ? <EmployerPanel/> : <EmployeePanel/>}
    </div>
  );
}


function EmployerPanel() {
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [creating,  setCreating]  = useState(false);
  const [form, setForm] = useState({
    name: "", employment_type: "monthly", base_salary: "",
    work_days_basis: 30, overtime_rate_per_hour: 0, night_shift_premium: 0,
  });
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(currentMonth());

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/hr/employees"); setEmployees(r.data.employees || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load employees"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createEmp = async () => {
    if (!form.name.trim() || !form.base_salary) { toast.error("Name + salary required"); return; }
    setCreating(true);
    try {
      const body = { ...form, base_salary: Number(form.base_salary),
                     overtime_rate_per_hour: Number(form.overtime_rate_per_hour),
                     night_shift_premium:    Number(form.night_shift_premium),
                     work_days_basis:        Number(form.work_days_basis) };
      await api.post("/hr/employees", body);
      toast.success("Employee added.");
      setForm({ name: "", employment_type: "monthly", base_salary: "",
                 work_days_basis: 30, overtime_rate_per_hour: 0, night_shift_premium: 0 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add employee"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="hr-employer-panel">
      {/* Add employee */}
      <Card className="rounded-3xl">
        <CardContent className="p-4 space-y-3">
          <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 inline-flex items-center gap-1">
            <UserPlus size={12}/> Add employee
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <Input placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                   className="h-9 text-sm" data-testid="hr-emp-name"/>
            <select className="h-9 rounded-lg border border-stone-200 px-2 text-sm"
                    value={form.employment_type}
                    onChange={e => setForm({...form, employment_type: e.target.value})}
                    data-testid="hr-emp-type">
              <option value="monthly">Monthly salary</option>
              <option value="daily">Daily wage</option>
            </select>
            <Input type="number" placeholder="Base salary / day rate"
                   value={form.base_salary}
                   onChange={e => setForm({...form, base_salary: e.target.value})}
                   className="h-9 text-sm" data-testid="hr-emp-salary"/>
            {form.employment_type === "monthly" && (
              <select className="h-9 rounded-lg border border-stone-200 px-2 text-sm"
                      value={form.work_days_basis}
                      onChange={e => setForm({...form, work_days_basis: e.target.value})}
                      data-testid="hr-emp-basis">
                <option value={30}>30-day basis</option>
                <option value={26}>26-day basis</option>
              </select>
            )}
            <Input type="number" placeholder="OT / hour"
                   value={form.overtime_rate_per_hour}
                   onChange={e => setForm({...form, overtime_rate_per_hour: e.target.value})}
                   className="h-9 text-sm" data-testid="hr-emp-ot"/>
            <Input type="number" placeholder="Night-shift premium"
                   value={form.night_shift_premium}
                   onChange={e => setForm({...form, night_shift_premium: e.target.value})}
                   className="h-9 text-sm" data-testid="hr-emp-night"/>
          </div>
          <Button onClick={createEmp} disabled={creating}
                  className="h-9 text-[11px] font-black bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="hr-emp-add">
            {creating ? <Loader2 size={12} className="animate-spin"/> : "Add"}
          </Button>
        </CardContent>
      </Card>

      {/* Employees list */}
      <Card className="rounded-3xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 inline-flex items-center gap-1">
              <Users size={12}/> Employees ({employees.length})
            </div>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)}
                   className="h-8 text-xs w-32" data-testid="hr-month"/>
          </div>
          {loading ? (
            <div className="text-xs text-stone-500 inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin"/> Loading…
            </div>
          ) : employees.length === 0 ? (
            <div className="text-xs text-stone-500">No employees yet. Add one above.</div>
          ) : (
            <div className="space-y-2">
              {employees.map(e => (
                <div key={e.employee_id}
                     className={`rounded-2xl border p-3 flex items-center justify-between cursor-pointer ${selected === e.employee_id ? "border-emerald-500 bg-emerald-50/40" : "border-stone-200 hover:bg-stone-50"}`}
                     onClick={() => setSelected(e.employee_id)}
                     data-testid={`hr-emp-row-${e.employee_id}`}>
                  <div>
                    <div className="text-sm font-bold text-stone-900">{e.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-400">
                      {e.employment_type} · ₹{e.base_salary}{e.employment_type === "monthly" ? `/mo · ${e.work_days_basis}-day basis` : "/day"}
                    </div>
                  </div>
                  <div className="text-[10px] text-stone-500">Tap to mark attendance →</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selected && <AttendancePanel key={selected} employeeId={selected} month={month}/>}
    </div>
  );
}


function AttendancePanel({ employeeId, month }) {
  const { t } = useI18n();
  const statusLabel = (k) => t(`hr.status_${k}`,
      { present:"Present", absent:"Absent", half_day:"Half day",
        paid_leave:"Paid leave", unpaid_leave:"Unpaid leave", holiday:"Holiday" }[k] || k);
  const [rows,    setRows]    = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [busy,    setBusy]    = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/hr/attendance", { params: { employee_id: employeeId, month } }),
        api.get(`/hr/payroll/${employeeId}`, { params: { month } }),
      ]);
      setRows(r1.data.attendance || []);
      setPayroll(r2.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load attendance"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [employeeId, month]);

  const mark = async (status, extras = {}) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.post("/hr/attendance", {
        employee_id: employeeId, date: today, status, ...extras,
      });
      toast.success(`Marked ${statusLabel(status)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark attendance"); }
  };

  const p = payroll?.result;

  return (
    <Card className="rounded-3xl" data-testid="hr-attendance-panel">
      <CardContent className="p-4 space-y-3">
        <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 inline-flex items-center gap-1">
          <CalendarDays size={12}/> Today · {new Date().toISOString().slice(0, 10)}
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_KEYS.map((k) => (
            <Button key={k} onClick={() => mark(k)}
                    variant="outline" size="sm" className="text-[11px] font-black"
                    data-testid={`hr-mark-${k}`}>{statusLabel(k)}</Button>
          ))}
          <Button onClick={() => mark("present", { overtime_hours: 2 })}
                  variant="outline" size="sm" className="text-[11px] font-black"
                  data-testid="hr-mark-present-ot">+ 2h OT</Button>
          <Button onClick={() => mark("present", { night_shift: true })}
                  variant="outline" size="sm" className="text-[11px] font-black"
                  data-testid="hr-mark-present-night">+ Night shift</Button>
        </div>

        {p && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3" data-testid="hr-payroll-preview">
            <div className="text-[11px] font-black uppercase tracking-widest text-emerald-700 mb-1 inline-flex items-center gap-1">
              <Wallet size={12}/> Payroll · {payroll.period}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div>Credited days<br/><b className="text-lg text-stone-900">{p.credited_days}</b></div>
              <div>Base pay<br/><b className="text-lg text-stone-900">₹{p.base_pay}</b></div>
              <div>OT pay<br/><b className="text-lg text-stone-900">₹{p.overtime_pay}</b></div>
              <div>Night premium<br/><b className="text-lg text-stone-900">₹{p.night_shift_pay}</b></div>
              <div>Early-leave deduct.<br/><b className="text-lg text-stone-900">-₹{p.early_leave_deduction}</b></div>
              <div className="col-span-2 md:col-span-3">Net pay<br/><b className="text-2xl text-emerald-700" data-testid="hr-net-pay">₹{p.net_pay}</b></div>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="text-[11px]">
            <div className="text-stone-500 mb-1">{rows.length} attendance rows this month</div>
            <div className="max-h-40 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100">
              {rows.slice(0, 40).map(r => (
                <div key={r.attendance_id} className="flex items-center justify-between px-2 py-1.5">
                  <span className="font-mono">{r.date}</span>
                  <span className="font-bold">{r.status}</span>
                  <span className="text-stone-500">
                    {r.overtime_hours ? `${r.overtime_hours}h OT` : ""}
                    {r.night_shift ? " · night" : ""}
                    {r.early_leave_hours ? ` · -${r.early_leave_hours}h` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {busy && <div className="text-xs text-stone-400 inline-flex items-center gap-1">
          <Loader2 size={11} className="animate-spin"/> Syncing…
        </div>}
      </CardContent>
    </Card>
  );
}


function EmployeePanel() {
  const { t } = useI18n();
  const statusLabel = (k) => t(`hr.status_${k}`,
      { present:"Present", absent:"Absent", half_day:"Half day",
        paid_leave:"Paid leave", unpaid_leave:"Unpaid leave", holiday:"Holiday" }[k] || k);
  const [employers, setEmployers] = useState([]);
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  useEffect(() => {
    (async () => {
      try {
        const e = await api.get("/hr/me/employers"); setEmployers(e.data.employers || []);
        const a = await api.get("/hr/me/attendance", { params: { month } });
        setRows(a.data.attendance || []);
      } catch (err) { toast.error(err?.response?.data?.detail || "Could not load"); }
    })();
  }, [month]);
  return (
    <div className="space-y-3" data-testid="hr-employee-panel">
      <Card className="rounded-3xl">
        <CardContent className="p-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 mb-2">My employers</div>
          {employers.length === 0 ? (
            <div className="text-xs text-stone-500">No employer has linked your Find2Near account yet.</div>
          ) : (
            <div className="space-y-2">
              {employers.map(e => (
                <div key={e.employee_id} className="text-sm">
                  <b>{e.name}</b>
                  <span className="text-stone-500 ml-2">
                    · {e.employment_type} · ₹{e.base_salary}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="rounded-3xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-stone-500">My attendance</div>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)}
                   className="h-8 text-xs w-32"/>
          </div>
          {rows.length === 0
            ? <div className="text-xs text-stone-500">No records for {month}.</div>
            : (
              <div className="max-h-72 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100">
                {rows.map(r => (
                  <div key={r.attendance_id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                    <span className="font-mono">{r.date}</span>
                    <span className="font-bold">{statusLabel(r.status)}</span>
                    <span className="text-stone-500">
                      {r.overtime_hours ? `${r.overtime_hours}h OT` : ""}
                      {r.night_shift ? " · night" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
